import {
  checkIsAllDomainsSelected,
  getNodeDescendantIds,
  getNodeWithDescendantsOptions,
  getSelectedDomains,
  reconcileParentSelections,
} from '@components/EntityDrawers/components/inputs/DomainDropdown/helpers';
import { CommonKeys } from '@components/EntityDrawers/constants/keys';
import { ISelectOption } from '@constants/entities/ui';
import useProjectId from '@hooks/useProjectId';
import { Stack, Switch, Typography } from '@mui/material';
import { TreeView } from '@mui/x-tree-view';
import { skipToken } from '@reduxjs/toolkit/query';
import { useGetProjectDomainsQuery } from '@store/services/projects';
import { renderTree } from '@views/Project/components/ScopeFilterModal/ScopeFilterModal';
import { SelectHandler } from '@views/Project/components/ScopeFilterModal/TreeItem';
import { buildTree } from '@views/Project/helpers';
import { useMemo, useRef, useState } from 'react';
import { Controller } from 'react-hook-form';

const DomainsPaperBody = () => {
  const projectId = useProjectId();
  const { data: projectDomains } = useGetProjectDomainsQuery(
    projectId || skipToken,
  );
  const [expanded, setExpanded] = useState<string[]>([]);

  /** Cache of individual domain selections before enterprise was toggled on */
  const cachedDomainsRef = useRef<ISelectOption[]>([]);

  const [wideDomain] = useMemo(() => {
    return buildTree(projectDomains ?? []);
  }, [projectDomains]);

  const nonEnterpriseTopLevelCount = wideDomain.children?.length ?? 0;
  const shouldAutoCollapseToWide = nonEnterpriseTopLevelCount > 1;

  /**
   * Build the full list of all individual domain options from the tree
   * (used when expanding from enterprise back to individual selections).
   */
  const buildAllDomainOptions = (): ISelectOption[] =>
    wideDomain.children?.flatMap((parent) => [
      { value: parent.id, label: parent.name },
      ...(parent.children?.map((child) => ({
        value: child.id,
        label: child.name,
      })) ?? []),
    ]) ?? [];

  const hanleClick = (nodeId: string) => {
    setExpanded((ids) =>
      ids.includes(nodeId)
        ? ids.filter((id) => id !== nodeId)
        : [...ids, nodeId],
    );
  };

  const handleSelect =
    (onChange: (value: any) => void, currentValue: any): SelectHandler =>
    ({ nodeId, checked }) => {
      const isCurrentlyWideSelected = currentValue?.some(
        (d: ISelectOption) => d.value === wideDomain.id,
      );

      const idsToRemove = new Set(getNodeDescendantIds(wideDomain, nodeId));
      const idsToExpand = getNodeDescendantIds(wideDomain, nodeId);

      const removeNodeAndDescendants = (domains: ISelectOption[]) =>
        domains.filter(
          (d) => !idsToRemove.has(String(d.value)) && d.value !== wideDomain.id,
        );

      // Unchecking while enterprise is active:
      // expand enterprise to individuals and remove only the clicked node (keep descendants selected)
      if (!checked && isCurrentlyWideSelected) {
        const expandedDomains =
          cachedDomainsRef.current.length > 0
            ? [...cachedDomainsRef.current]
            : buildAllDomainOptions();

        setExpanded((prev) => Array.from(new Set([...prev, ...idsToExpand])));
        onChange(removeNodeAndDescendants(expandedDomains));
        cachedDomainsRef.current = [];
        return;
      }

      if (checked) {
        // Select clicked node + all descendants
        const toAdd = getNodeWithDescendantsOptions(wideDomain, nodeId);

        setExpanded((prev) => Array.from(new Set([...prev, ...idsToExpand])));

        const mergedMap = new Map<string, ISelectOption>();
        (currentValue ?? []).forEach((item: ISelectOption) =>
          mergedMap.set(String(item.value), item),
        );
        toAdd.forEach((item) => mergedMap.set(String(item.value), item));

        let newDomainsArray = Array.from(mergedMap.values());

        newDomainsArray = reconcileParentSelections(
          newDomainsArray,
          projectDomains ?? [],
        );

        const { isAllDomainsSelected, isWideSelected } =
          checkIsAllDomainsSelected({
            projectDomains: projectDomains ?? [],
            selectedDomains: newDomainsArray,
          });

        if (
          isAllDomainsSelected &&
          !isWideSelected &&
          shouldAutoCollapseToWide
        ) {
          cachedDomainsRef.current = [...newDomainsArray];
          onChange([{ value: wideDomain.id, label: wideDomain.name }]);
          return;
        }

        onChange(newDomainsArray);
      } else {
        setExpanded((prev) => Array.from(new Set([...prev, ...idsToExpand])));
        onChange(removeNodeAndDescendants(currentValue ?? []));
      }
    };

  return (
    <Stack>
      <Controller
        render={({ field: { value, onChange } }) => {
          const isWideSelected = value?.some(
            (domain: ISelectOption) => domain.value === wideDomain.id,
          );

          const selectedDomains = getSelectedDomains({
            selectedDomains: value,
            wideDomain,
          });

          return (
            <>
              <Stack
                direction="row"
                px={3}
                py={2}
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography color="grey.900">{wideDomain.name}</Typography>

                <Switch
                  checked={isWideSelected}
                  onChange={(_, checked) => {
                    if (checked) {
                      // Cache current selections, set form to enterprise only
                      cachedDomainsRef.current = (value ?? []).filter(
                        (d: ISelectOption) => d.value !== wideDomain.id,
                      );
                      onChange([
                        {
                          value: wideDomain.id,
                          label: wideDomain.name,
                        },
                      ]);
                    } else {
                      // Restore cached individual selections
                      onChange(cachedDomainsRef.current);
                      cachedDomainsRef.current = [];
                    }
                  }}
                />
              </Stack>

              <TreeView
                expanded={expanded}
                disableSelection
                multiSelect
                selected={selectedDomains}
              >
                {wideDomain.children?.map((item) =>
                  renderTree(
                    item,
                    [],
                    handleSelect(onChange, value),
                    hanleClick,
                    undefined,
                    {
                      p: '8px 12px',
                    },
                  ),
                )}
              </TreeView>
            </>
          );
        }}
        name={CommonKeys.Domains}
      />
    </Stack>
  );
};

export default DomainsPaperBody;
